import { DEFAULT_LAUNCHER_ITEMS, launcherItemKeys } from './launcherItems';
// Central operational feature flags. Every workflow reads from this module so
// Settings switches change real behaviour instead of becoming display-only.
export const GATE_PASS_DEFECT_FIELDS = Object.freeze([
  ['rust', 'Rust'],
  ['appleMaggot', 'Apple Maggot'],
  ['scabs', 'Scabs'],
  ['calciumSpot', 'Calcium Spot'],
  ['sunBurn', 'Sun Burn'],
  ['bitterRut', 'Bitter Rot'],
  ['sanjoseScale', 'San Jose Scale'],
  ['hailDamage', 'Hail Damage'],
  ['stalkAbsent', 'Stalk Absent'],
  ['sooty', 'Sooty'],
  ['sootyBlotch', 'Sooty Blotch'],
  ['bitterPit', 'Bitter Pit'],
  ['touching', 'Touching'],
  ['lenticalBlotch', 'Lentical Blotch'],
  ['insectCore', 'Insect Core'],
  ['limbRub', 'Limb Rub'],
  ['bruising', 'Bruising'],
  ['waterCore', 'Water Core'],
  ['pigmyRoll', 'Pigmy/Roll'],
  ['mixRipe', 'Mix Ripe'],
  ['others', 'Others'],
]);

export const DEFAULT_GRADE_TYPES = Object.freeze([
  { id: 'extra_premium', name: 'Extra Premium', code: 'EP', active: true, sortOrder: 10 },
  { id: 'premium', name: 'Premium', code: 'P', active: true, sortOrder: 20 },
  { id: 'grade_a', name: 'Grade A', code: 'A', active: true, sortOrder: 30 },
  { id: 'grade_b', name: 'Grade B', code: 'B', active: true, sortOrder: 40 },
  { id: 'grade_c', name: 'Grade C', code: 'C', active: true, sortOrder: 50 },
  { id: 'juice', name: 'Juice', code: 'JUICE', active: true, sortOrder: 60 },
  { id: 'reject', name: 'Reject', code: 'REJECT', active: true, sortOrder: 70 },
]);

export const DEFAULT_OUTWARD_PROCESSING_TYPES = Object.freeze({
  direct_dispatch: true,
  grading_only: true,
  grading_packing: true,
  packing_pregraded: true,
});

export const DEFAULT_GRADING_LINES = Object.freeze([
  { id: 'line_1', name: 'Line 1', code: 'L1', active: true, sortOrder: 10, capacityCratesPerHour: 0 },
]);

export const DEFAULT_OPERATIONAL_SETTINGS = Object.freeze({
  captureVarietyAtInward: true,
  // Original full Inward/Gate Pass form is the only supported layout.
  defaultInwardDesign: 'advanced',
  allowInwardDesignOverride: false,
  rememberEmployeeInwardDesign: false,
  allowCreateVarietyAtInward: true,
  requireVarietyCratesAtInward: true,
  allowMultipleAgreementsAtInward: true,
  allowAgreementCrateOverage: true,
  allowAgreementVarietyOverage: true,
  requireAgreementOverageReason: false,
  manyToManyGrowerSubGrower: true,
  allowInspectionVarietyEdit: true,
  allowInspectionVarietySplit: true,
  allowInspectionVarietyMerge: true,
  allowInspectionAddVariety: true,
  requireInspectionCorrectionReason: true,
  requireInspectionCrateVarianceReason: true,
  packingPackagesEnabled: true,
  customerProvidedPackingEnabled: true,
  crateReconciliationEnabled: true,
  smartStorageAssignmentEnabled: true,
  allowMixedOutwardProcessing: true,
  gradingEnabled: true,
  allowMultiLotGrading: true,
  allowMultiGrowerGrading: true,
  allowPartialLotGrading: true,
  allowMixedVarietyGrading: true,
  gradingLinesEnabled: true,
  allowMultipleGradingLinesAtOutward: true,
  gradingLines: DEFAULT_GRADING_LINES,
  packingOptional: true,
  outwardProcessingTypes: DEFAULT_OUTWARD_PROCESSING_TYPES,
  gradeTypes: DEFAULT_GRADE_TYPES,
  // Queue number is informational/recommended ordering only. It never blocks a ready record.
  queueMode: 'flexible',
  adminQueueOverride: true,
  queueOverrideReasonRequired: false,
  quickLauncherEnabled: true,
  quickLauncherItems: DEFAULT_LAUNCHER_ITEMS,
  storageBillingMode: 'lot_inward_date', // legacy fallback only; new agreements store their own billing policy
  // Lot/identity timing is company-configurable. 'after_inward' creates a provisional
  // journey label immediately after Inward and reuses that identity later.
  lotLabelTiming: 'after_inspection', // after_inward | after_gatepass | after_inspection
  // Admin-controlled sequential Lot identity. Example: LOT-1, LOT-2, LOT-3.
  lotNumberPrefix: 'LOT',
  lotNumberSequenceStart: 1,
  // Changing this revision intentionally starts a new counter configuration while
  // still refusing to issue a Lot No that already exists.
  lotNumberSequenceRevision: 0,
  inwardCodeMode: 'qr', // Gate Pass is QR-first; lot labels can continue using barcode independently.
  gatePassShowPreliminaryInspection: true,
  gatePassPrintConfig: {
    identityQuality: true, declaredVariety: true, verifiedVariety: true, verifiedCrates: true,
    grade: true, size: true, quality: true, condition: true, pressure: true, fmt: true, fit: true,
    color: true, starch: true, sugar: true, defects: true, damagePercent: true, rottenPercent: true,
    moisturePercent: true, temperatureC: true, humidityPercent: true, remarks: true,
    agreementServices: true, vehicleDetails: true, partyDetails: true, agreementDetails: true, workflowDetails: true
  },
  gatePassDefectFields: Object.fromEntries(GATE_PASS_DEFECT_FIELDS.map(([key]) => [key, true])),
  reportPrint: { showLogo: true, showCompanyDetails: true, showFilters: true, showSignatures: true, compactRows: true },
  offline: {
    enabled: true,
    autoSync: true,
    prefetchOnLogin: true,
    keepUnsyncedUntilSynced: true,
    historyRetentionDays: 90,
    imageRetentionDays: 30,
    serverMode: 'online_only', // online_only | offline_server_only | hybrid
    localServer: {
      enabled: false,
      url: '',
      storeId: '',
      serverId: '',
      token: '',
      timeoutMs: 3000,
      autoDiscovery: true,
      serverName: '',
      lanHostname: 'frostflow.local',
      preferredInterface: '',
      allowLanClients: true,
      requireDeviceApproval: true,
    },
    cloud: { enabled: true, allowFallback: true },
    hybrid: { preferredSource: 'local', switchBackAutomatically: true },
    sync: { enabled: true, automatic: true, intervalSeconds: 30, pullIntervalSeconds: 300, syncFiles: true, backgroundSync: true, uploadLocal: true, downloadCloud: true },
    backup: { enabled: true, hour: 23, keepDaily: 14, keepWeekly: 8, keepMonthly: 12, includeFiles: false, mirrorPath: '' },
    security: { requireDeviceApproval: true, offlineLoginValidityDays: 30 },
    files: { storeLocallyFirst: true, keepLocalAfterSync: true, retentionDays: 0 },
  },
});

const boolValue = (value, fallback = false) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  if (['true','1','yes','on','enabled'].includes(normalized)) return true;
  if (['false','0','no','off','disabled'].includes(normalized)) return false;
  return fallback;
};

export const normalizeOperationalSettings = (value = {}) => {
  const raw = value || {};
  const booleanDefaults = Object.fromEntries(
    Object.entries(DEFAULT_OPERATIONAL_SETTINGS).filter(([, current]) => typeof current === 'boolean')
  );
  const normalizedBooleans = Object.fromEntries(
    Object.entries(booleanDefaults).map(([key, fallback]) => [key, boolValue(raw[key], fallback)])
  );
  const processing = Object.fromEntries(
    Object.entries(DEFAULT_OUTWARD_PROCESSING_TYPES).map(([key, fallback]) => [
      key,
      boolValue(raw.outwardProcessingTypes?.[key], fallback),
    ])
  );
  if (normalizedBooleans.gradingEnabled === false) {
    processing.grading_only = false;
    processing.grading_packing = false;
  }
  const grades = Array.isArray(raw.gradeTypes) && raw.gradeTypes.length
    ? raw.gradeTypes.map((row, index) => ({
        id: row.id || row.code || `grade_${index + 1}`,
        name: row.name || row.label || row.code || `Grade ${index + 1}`,
        code: row.code || row.name || `G${index + 1}`,
        active: boolValue(row.active, true),
        sortOrder: Number(row.sortOrder ?? ((index + 1) * 10)),
      })).sort((a,b) => a.sortOrder - b.sortOrder)
    : DEFAULT_GRADE_TYPES.map((row) => ({ ...row }));

  const defaultInwardDesign = 'advanced';

  const lotLabelTiming = ['after_inward','after_gatepass','after_inspection'].includes(String(raw.lotLabelTiming || '').trim())
    ? String(raw.lotLabelTiming).trim()
    : DEFAULT_OPERATIONAL_SETTINGS.lotLabelTiming;
  // Gate Pass documents are QR-only. Legacy values remain readable but runtime output normalizes to QR.
  const inwardCodeMode = 'qr';
  const lotNumberPrefix = String(raw.lotNumberPrefix || DEFAULT_OPERATIONAL_SETTINGS.lotNumberPrefix || 'LOT')
    .trim().replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 12) || 'LOT';
  const lotNumberSequenceStart = Math.max(1, Math.floor(Number(raw.lotNumberSequenceStart ?? DEFAULT_OPERATIONAL_SETTINGS.lotNumberSequenceStart ?? 1) || 1));
  const lotNumberSequenceRevision = Math.max(0, Math.floor(Number(raw.lotNumberSequenceRevision ?? DEFAULT_OPERATIONAL_SETTINGS.lotNumberSequenceRevision ?? 0) || 0));

  return {
    ...DEFAULT_OPERATIONAL_SETTINGS,
    ...raw,
    ...normalizedBooleans,
    defaultInwardDesign,
    lotLabelTiming,
    lotNumberPrefix,
    lotNumberSequenceStart,
    lotNumberSequenceRevision,
    inwardCodeMode,
    // Queue is permanently non-blocking. Preserve legacy values in Firestore,
    // but normalize runtime behaviour to flexible so old tenants cannot remain locked.
    queueMode: 'flexible',
    adminQueueOverride: true,
    queueOverrideReasonRequired: false,
    // Company-level workflow choice: when false, Inward captures Product + total crates only
    // and Quality Inspection becomes the authoritative variety-identification stage.
    captureVarietyAtInward: normalizedBooleans.captureVarietyAtInward,
    // Variety-crate reconciliation only applies when varieties are captured at Inward.
    requireVarietyCratesAtInward: normalizedBooleans.captureVarietyAtInward
      ? normalizedBooleans.requireVarietyCratesAtInward
      : false,
    gradingLines: (() => {
      const source = Array.isArray(raw.gradingLines) && raw.gradingLines.length ? raw.gradingLines : DEFAULT_GRADING_LINES;
      const normalized = source.map((row, index) => ({
        id: String(row?.id || row?.code || `line_${index + 1}`).trim() || `line_${index + 1}`,
        name: String(row?.name || row?.label || `Line ${index + 1}`).trim() || `Line ${index + 1}`,
        code: String(row?.code || `L${index + 1}`).trim().toUpperCase() || `L${index + 1}`,
        active: boolValue(row?.active, true),
        sortOrder: Number(row?.sortOrder ?? ((index + 1) * 10)),
        capacityCratesPerHour: Math.max(0, Number(row?.capacityCratesPerHour || row?.capacity || 0) || 0),
      })).sort((a,b) => a.sortOrder - b.sortOrder);
      return normalized.length ? normalized : DEFAULT_GRADING_LINES.map((row) => ({ ...row }));
    })(),
    outwardProcessingTypes: processing,
    gradeTypes: grades,
    quickLauncherItems: (() => {
      const allowed = new Set(launcherItemKeys());
      const selected = Array.isArray(raw.quickLauncherItems) ? raw.quickLauncherItems.filter((key) => allowed.has(key)) : [];
      return selected.length ? Array.from(new Set(selected)) : [...DEFAULT_LAUNCHER_ITEMS];
    })(),
    gatePassPrintConfig: Object.fromEntries(Object.entries(DEFAULT_OPERATIONAL_SETTINGS.gatePassPrintConfig).map(([key,fallback]) => [key, boolValue(raw.gatePassPrintConfig?.[key], fallback)])),
    gatePassDefectFields: Object.fromEntries(GATE_PASS_DEFECT_FIELDS.map(([key]) => [key, boolValue(raw.gatePassDefectFields?.[key], true)])),
    reportPrint: Object.fromEntries(Object.entries(DEFAULT_OPERATIONAL_SETTINGS.reportPrint).map(([key,fallback]) => [key, boolValue(raw.reportPrint?.[key], fallback)])),
    offline: {
      enabled: boolValue(raw.offline?.enabled, DEFAULT_OPERATIONAL_SETTINGS.offline.enabled),
      autoSync: boolValue(raw.offline?.autoSync, DEFAULT_OPERATIONAL_SETTINGS.offline.autoSync),
      prefetchOnLogin: boolValue(raw.offline?.prefetchOnLogin, DEFAULT_OPERATIONAL_SETTINGS.offline.prefetchOnLogin),
      keepUnsyncedUntilSynced: true,
      historyRetentionDays: Math.max(0, Number(raw.offline?.historyRetentionDays ?? DEFAULT_OPERATIONAL_SETTINGS.offline.historyRetentionDays) || DEFAULT_OPERATIONAL_SETTINGS.offline.historyRetentionDays),
      imageRetentionDays: Math.max(0, Number(raw.offline?.imageRetentionDays ?? DEFAULT_OPERATIONAL_SETTINGS.offline.imageRetentionDays) || DEFAULT_OPERATIONAL_SETTINGS.offline.imageRetentionDays),
      serverMode: ['online_only','offline_server_only','hybrid'].includes(String(raw.offline?.serverMode || ''))
        ? String(raw.offline.serverMode)
        : DEFAULT_OPERATIONAL_SETTINGS.offline.serverMode,
      localServer: {
        ...DEFAULT_OPERATIONAL_SETTINGS.offline.localServer,
        ...(raw.offline?.localServer || {}),
        enabled: boolValue(raw.offline?.localServer?.enabled, DEFAULT_OPERATIONAL_SETTINGS.offline.localServer.enabled),
        timeoutMs: Math.max(500, Number(raw.offline?.localServer?.timeoutMs ?? DEFAULT_OPERATIONAL_SETTINGS.offline.localServer.timeoutMs) || 3000),
        autoDiscovery: boolValue(raw.offline?.localServer?.autoDiscovery, DEFAULT_OPERATIONAL_SETTINGS.offline.localServer.autoDiscovery),
        allowLanClients: boolValue(raw.offline?.localServer?.allowLanClients, true),
        requireDeviceApproval: boolValue(raw.offline?.localServer?.requireDeviceApproval, true),
      },
      cloud: {
        ...DEFAULT_OPERATIONAL_SETTINGS.offline.cloud,
        ...(raw.offline?.cloud || {}),
        enabled: boolValue(raw.offline?.cloud?.enabled, true),
        allowFallback: boolValue(raw.offline?.cloud?.allowFallback, true),
      },
      hybrid: {
        ...DEFAULT_OPERATIONAL_SETTINGS.offline.hybrid,
        ...(raw.offline?.hybrid || {}),
        preferredSource: 'local',
        switchBackAutomatically: boolValue(raw.offline?.hybrid?.switchBackAutomatically, true),
      },
      sync: {
        ...DEFAULT_OPERATIONAL_SETTINGS.offline.sync,
        ...(raw.offline?.sync || {}),
        enabled: boolValue(raw.offline?.sync?.enabled, true),
        automatic: boolValue(raw.offline?.sync?.automatic, true),
        intervalSeconds: Math.max(5, Number(raw.offline?.sync?.intervalSeconds ?? 30) || 30),
        pullIntervalSeconds: Math.max(60, Number(raw.offline?.sync?.pullIntervalSeconds ?? 300) || 300),
        syncFiles: boolValue(raw.offline?.sync?.syncFiles, true),
        backgroundSync: boolValue(raw.offline?.sync?.backgroundSync, true),
        uploadLocal: boolValue(raw.offline?.sync?.uploadLocal, true),
        downloadCloud: boolValue(raw.offline?.sync?.downloadCloud, true),
      },
      backup: {
        ...DEFAULT_OPERATIONAL_SETTINGS.offline.backup,
        ...(raw.offline?.backup || {}),
        enabled: boolValue(raw.offline?.backup?.enabled, true),
        hour: Math.max(0, Math.min(23, Number(raw.offline?.backup?.hour ?? 23) || 23)),
        keepDaily: Math.max(1, Number(raw.offline?.backup?.keepDaily ?? 14) || 14),
        keepWeekly: Math.max(1, Number(raw.offline?.backup?.keepWeekly ?? 8) || 8),
        keepMonthly: Math.max(1, Number(raw.offline?.backup?.keepMonthly ?? 12) || 12),
        includeFiles: boolValue(raw.offline?.backup?.includeFiles, false),
        mirrorPath: String(raw.offline?.backup?.mirrorPath || ''),
      },
      security: {
        ...DEFAULT_OPERATIONAL_SETTINGS.offline.security,
        ...(raw.offline?.security || {}),
        requireDeviceApproval: boolValue(raw.offline?.security?.requireDeviceApproval, true),
        offlineLoginValidityDays: Math.max(1, Number(raw.offline?.security?.offlineLoginValidityDays ?? 30) || 30),
      },
      files: {
        ...DEFAULT_OPERATIONAL_SETTINGS.offline.files,
        ...(raw.offline?.files || {}),
        storeLocallyFirst: boolValue(raw.offline?.files?.storeLocallyFirst, true),
        keepLocalAfterSync: boolValue(raw.offline?.files?.keepLocalAfterSync, true),
        retentionDays: Math.max(0, Number(raw.offline?.files?.retentionDays ?? 0) || 0),
      },
    },
  };
};

export const enabledGradeTypes = (settings = {}) => normalizeOperationalSettings(settings)
  .gradeTypes.filter((row) => row.active !== false);


export const enabledGradingLines = (settings = {}) => {
  const normalized = normalizeOperationalSettings(settings);
  return (normalized.gradingLines || []).filter((row) => row.active !== false);
};

export const enabledOutwardProcessingTypes = (settings = {}) => {
  const normalized = normalizeOperationalSettings(settings);
  return Object.entries(normalized.outwardProcessingTypes)
    .filter(([, enabled]) => enabled === true)
    .map(([key]) => key);
};

export const canProcessOutOfQueue = () => true;

export const outwardProcessingLabel = (mode) => ({
  direct_dispatch: 'Direct Dispatch',
  grading_only: 'Grading Only',
  grading_packing: 'Grading + Packing',
  packing_pregraded: 'Packing Previously Graded Stock',
}[mode] || String(mode || '').replaceAll('_', ' '));

export default normalizeOperationalSettings;
